import { useEffect, useMemo, useState } from 'react';
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCorners, useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { App, Button, Tooltip, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { BoardStage, DealItem } from '@crm/shared';
import { DealCard } from './DealCard';
import { money, useMoveDeal, weightedValue, type Board } from '../../features/deals/api';
import { errorMessage } from '../../api/client';
import { palette } from '../../app/theme';

const KIND_DOT = { OPEN: palette.primary, WON: palette.success, LOST: palette.error } as const;

function SortableCard({ deal }: { deal: DealItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: deal.id, data: { stageId: deal.stageId } });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.4 : 1, marginBottom: 10 }} {...attributes} {...listeners}>
      <DealCard deal={deal} />
    </div>
  );
}

function Column({ stage, currency, onAdd }: { stage: BoardStage; currency: string; onAdd: (stageId: string) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, data: { stageId: stage.id, column: true } });
  const count = stage.dealCount ?? stage.deals.length;
  return (
    <div
      style={{
        width: 288,
        flex: '0 0 288px',
        background: isOver ? palette.primarySoft : '#F3F4F6',
        border: `1px solid ${isOver ? '#C9D6FB' : 'transparent'}`,
        borderRadius: 14,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 280px)',
        transition: 'background 120ms',
      }}
    >
      <div style={{ padding: '14px 14px 10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ flex: 'none', width: 8, height: 8, borderRadius: 4, background: stage.color ?? KIND_DOT[stage.kind] }} />
            <Typography.Text strong ellipsis>{stage.name}</Typography.Text>
            <span style={{ flex: 'none', minWidth: 22, height: 20, padding: '0 7px', borderRadius: 10, background: palette.surface, color: palette.textSecondary, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{count}</span>
          </span>
          <Tooltip title="Add a deal to this stage">
            <Button type="text" size="small" aria-label={`Add deal to ${stage.name}`} icon={<PlusOutlined />} onClick={() => onAdd(stage.id)} />
          </Tooltip>
        </div>
        {stage.probability !== null && (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, paddingLeft: 16 }}>{stage.probability}% probability</Typography.Text>
        )}
      </div>
      <div ref={setNodeRef} style={{ padding: '0 10px 10px', overflowY: 'auto', flex: 1, minHeight: 72 }}>
        <SortableContext items={stage.deals.map((d) => d.id)} strategy={verticalListSortingStrategy}>
          {stage.deals.map((d) => <SortableCard key={d.id} deal={d} />)}
        </SortableContext>
        {!stage.deals.length && (
          <div style={{ textAlign: 'center', padding: '18px 8px', border: `1px dashed ${palette.borderStrong}`, borderRadius: 12, marginBottom: 8, background: 'rgba(255,255,255,0.5)' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>No deals in this stage. Drag one here.</Typography.Text>
          </div>
        )}
        <Button type="text" size="small" block icon={<PlusOutlined />} onClick={() => onAdd(stage.id)} style={{ color: palette.textSecondary, height: 34 }}>Add deal</Button>
      </div>
      <div style={{ padding: '10px 14px 12px', borderTop: `1px solid ${palette.borderStrong}`, display: 'grid', gap: 2, fontSize: 12 }}>
        <span style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Total</Typography.Text>
          <Typography.Text strong style={{ fontSize: 12 }}>{money(stage.totalValue, currency)}</Typography.Text>
        </span>
        {stage.probability !== null && (
          <span style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Estimated ({stage.probability}%)</Typography.Text>
            <Typography.Text strong style={{ fontSize: 12 }}>{money(weightedValue(stage), currency)}</Typography.Text>
          </span>
        )}
      </div>
    </div>
  );
}

/** Kanban board. Drops call the move endpoint; the board state is updated optimistically. */
export function Kanban({ board, currency, onAdd }: { board: Board; currency: string; onAdd: (stageId: string) => void }) {
  const { message } = App.useApp();
  const move = useMoveDeal();
  const [stages, setStages] = useState<BoardStage[]>(board.stages);
  const [active, setActive] = useState<DealItem | null>(null);
  useEffect(() => setStages(board.stages), [board]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // space to pick up a card, arrow keys to move it, space to drop
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const dealsById = useMemo(() => new Map(stages.flatMap((s) => s.deals.map((d) => [d.id, d] as const))), [stages]);

  const onDragStart = ({ active: a }: DragStartEvent) => setActive(dealsById.get(String(a.id)) ?? null);

  const onDragEnd = ({ active: a, over }: DragEndEvent) => {
    setActive(null);
    if (!over) return;
    const dealId = String(a.id);
    const deal = dealsById.get(dealId);
    if (!deal) return;
    const overId = String(over.id);
    const overStage = stages.find((s) => s.id === overId);
    const targetStage = overStage ?? stages.find((s) => s.deals.some((d) => d.id === overId));
    if (!targetStage) return;
    let index = overStage ? targetStage.deals.length : targetStage.deals.findIndex((d) => d.id === overId);
    const fromStage = stages.find((s) => s.id === deal.stageId)!;
    const fromIndex = fromStage.deals.findIndex((d) => d.id === dealId);
    if (targetStage.id === fromStage.id) {
      if (index === fromIndex || index === -1) return;
    } else if (overStage) {
      index = targetStage.deals.length;
    }
    // optimistic local reorder
    setStages((prev) =>
      prev.map((s) => {
        if (s.id === fromStage.id && s.id === targetStage.id) {
          const list = s.deals.filter((d) => d.id !== dealId);
          list.splice(index, 0, { ...deal });
          return { ...s, deals: list };
        }
        if (s.id === fromStage.id) return { ...s, deals: s.deals.filter((d) => d.id !== dealId), totalValue: s.totalValue - (deal.value ?? 0), dealCount: (s.dealCount ?? s.deals.length) - 1 };
        if (s.id === targetStage.id) {
          const list = [...s.deals];
          list.splice(index, 0, { ...deal, stageId: s.id, stage: { id: s.id, name: s.name, kind: s.kind, color: s.color, probability: s.probability } });
          return { ...s, deals: list, totalValue: s.totalValue + (deal.value ?? 0), dealCount: (s.dealCount ?? s.deals.length) + 1 };
        }
        return s;
      }),
    );
    move.mutateAsync({ id: dealId, stageId: targetStage.id, sortIndex: index }).catch((e) => {
      message.error(errorMessage(e));
      setStages(board.stages);
    });
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
      <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
        {stages.map((s) => <Column key={s.id} stage={s} currency={currency} onAdd={onAdd} />)}
      </div>
      <DragOverlay>{active ? <div style={{ width: 266 }}><DealCard deal={active} dragging /></div> : null}</DragOverlay>
    </DndContext>
  );
}
